import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./tscReset.css";

// "Reset demo data": puts every TSC demo order back to how it started and clears password resets, the same as
// running public.tsc_cx_reset_demo() in the SQL editor. The page calls the Supabase Edge Function tsc-demo-reset
// (no database key in the page); the database refuses a second reset within a minute. Both TSC demos share the
// orders, so one reset covers /tsc and /multilingual-support-tsc.

const RESET_URL = "https://hexxqgjyiokqzjdfdvuy.supabase.co/functions/v1/tsc-demo-reset";
// The "Within 59 min" orders are pinned 12 to 25 minutes old at a reset, so they leave the window ~34 minutes later.
const STALE_MIN = 30;

type Phase = "idle" | "confirm" | "busy" | "error";

function ago(iso: string | null, now: number): string {
  if (!iso) return "";
  const min = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}

export function TscResetDemo({ variant = "nav", disabled = false, disabledNote }: {
  variant?: "nav" | "console";
  disabled?: boolean;
  disabledNote?: string;
}) {
  const [last, setLast] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [note, setNote] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      const r = await fetch(RESET_URL);
      const d = await r.json();
      if (d?.last_reset_at) setLast(d.last_reset_at);
    } catch { /* the button still works without the status */ }
  }, []);

  useEffect(() => {
    void load();
    const tick = setInterval(() => setNow(Date.now()), 30000);
    const poll = setInterval(() => void load(), 120000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [load]);

  useEffect(() => {
    if (phase !== "confirm" && phase !== "error") return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setPhase("idle"); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase]);

  const reset = async () => {
    setPhase("busy");
    setNote(null);
    try {
      const r = await fetch(RESET_URL, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.error || `HTTP ${r.status}`);
      if (d.last_reset_at) setLast(d.last_reset_at);
      setNow(Date.now());
      if (d.reset) { setPhase("idle"); return; }
      setNote(`Someone reset it a moment ago, so the data is already fresh. You can reset again in ${d.retry_in_s ?? 60} seconds.`);
      setPhase("error");
    } catch {
      setNote("Couldn't reset the demo data. Try again in a moment.");
      setPhase("error");
    }
  };

  const minutes = last ? (now - new Date(last).getTime()) / 60000 : 0;
  const stale = !!last && minutes > STALE_MIN;
  const status = last ? `Reset ${ago(last, now)}` : "";

  return (
    <span className={`tsc-reset tsc-reset-${variant}`}>
      <button
        type="button"
        className="tsc-reset-btn"
        onClick={() => { setNote(null); setPhase("confirm"); }}
        disabled={disabled || phase === "busy"}
        title={disabled ? disabledNote : "Put every demo order back to how it started"}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 12a9 9 0 1 0 3-6.7" /><polyline points="3 3 3 9 9 9" />
        </svg>
        {phase === "busy" ? "Resetting…" : "Reset demo data"}
      </button>
      {status && (
        <span className={`tsc-reset-ago${stale ? " stale" : ""}`} title={stale ? "Orders marked \"Within 59 min\" may be past the window. Reset before you test." : undefined}>
          {status}
        </span>
      )}
      {(phase === "confirm" || phase === "busy" || phase === "error") && createPortal(
        <div className="tsc-reset-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && phase !== "busy") setPhase("idle"); }}>
          <div className="tsc-reset-dialog" role="dialog" aria-modal="true" aria-labelledby="tsc-reset-h">
            <h2 id="tsc-reset-h">Reset the demo data?</h2>
            <p>Every order goes back to how it started and password resets are cleared, for everyone testing right now. Don't reset while someone is on a call.</p>
            {last && (
              <p className={`tsc-reset-last${stale ? " stale" : ""}`}>
                Last reset {ago(last, now)}.{stale ? " Orders marked \"Within 59 min\" may be past the window until you reset." : ""}
              </p>
            )}
            {note && <p className="tsc-reset-note">{note}</p>}
            <div className="tsc-reset-actions">
              <button type="button" className="tsc-reset-cancel" onClick={() => setPhase("idle")} disabled={phase === "busy"}>
                {phase === "error" ? "Close" : "Cancel"}
              </button>
              {phase !== "error" && (
                <button type="button" className="tsc-reset-go" onClick={reset} disabled={phase === "busy"} autoFocus>
                  {phase === "busy" ? "Resetting…" : "Reset"}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}
